import * as React from 'react'
import * as _ from 'lodash'
import { Button, ButtonBar } from '../../../shared/components/Button'
import { ContentContainerBody, SplitContentContainer } from '../../../shared/components/Content'
import { SideInformation } from '../../../shared/components/SideInformation'
import { breakpoint, styled } from '../../../shared/styled-components'
import { CalculatorStoreInstance } from '../../../stores/CalculatorStore'
import { Jaw } from '../../../shared/Constants'
import { inject, observer } from 'mobx-react'
import { autobind } from 'core-decorators'
import { Headlines, InnerContainer } from '../../../shared/components/Elements'
import { Headline, HeadlineCSS, SubHeadline } from '../../../shared/components/Headlines'
import { Routes } from '../../Routes'
import { MaterialContent } from './material/MaterialContent'
import { SegmentedProgressBar } from './result/SegmentedProgressBar'

interface IProps {
  store?: CalculatorStoreInstance
  history?: History
  jaw: Jaw
}

@inject('store')
@observer
export class MaterialView extends React.Component<IProps> {
  // -------------------------------------------------------------------------- LIFECYCLE

  componentDidMount () {
    const { store, jaw } = this.props
    const requirement = store!.offer.getJawRequirement(jaw)
    const jawRoute = Routes.getJawRoute(jaw)
    if (!requirement) {
      return store!.redirectToRoute(jawRoute)
    }
  }

  // -------------------------------------------------------------------------- EVENTS

  @autobind
  private _onMaterialChange (material: 'normal'|'premium') {
    const { store, jaw } = this.props
    const requirement = store!.offer.getJawRequirement(jaw)
    requirement.setMaterial(material)
  }

  // -------------------------------------------------------------------------- RENDERING

  private _renderMaterialContent () {
    const { store, jaw } = this.props
    const requirement = store!.offer.getJawRequirement(jaw)
    switch (requirement.material) {
      case 'premium':
        return (<MaterialContent
          title='Premium Material'
          imageName='premium'
          recommended={true}
          onNextClick={() => store!.goForward()}>
          Hier werden nur die besten Materialien verwendet. Entweder eine edelmetallhaltige Legierung, welche im weiteren Verlauf der Implantatversorgung keramisch verblendet wird, oder eine vollkeramische Konstruktion ohne Metall. Dies kann insbesondere bei Metallallergien oder höchsten ästhetischen Ansprüchen (z.B. in der Oberkiefer-Front) sinnvoll sein.
        </MaterialContent>)
      case 'normal':
        return (<MaterialContent
          title='Basis Material'
          imageName='basis'
          onNextClick={() => store!.goForward()}>
          Wenn Sie die Qualitätsstufe „Basis“ wählen, wird für den Zahnersatz in der Regel eine Metalllegierung verwendet, die kein Edelmetall enthält. Das Metallgerüst wird jedoch keramisch genauso verblendet wie bei goldhaltigen Materialien. Ästhetik und Haltbarkeit sind also absolut vergleichbar. Die meisten Patienten wählen diese Art der Versorgung.
        </MaterialContent>)
    }
  }

  render (): React.ReactNode {
    const { store, jaw } = this.props
    const requirement = store!.offer.getJawRequirement(jaw)
    const recommendation = store!.offer.getJawRecommendations(jaw)[0]

    if (!requirement || !recommendation) return <div />

    const premiumAvailable = recommendation.attachmentType !== 'locator'

    return (<InnerContainer padded>
      <Headlines>
        <Headline centered>Material</Headline>
        <SubHeadline centered>Welche Art von Material bevorzugen Sie?</SubHeadline>
      </Headlines>

      <SplitContentContainer>
        <ContentContainerBody vertical>
          <MaterialSelection>
            {premiumAvailable && <PremiumMaterial selected={requirement.material === 'premium'} onClick={this._onMaterialChange.bind(this, 'premium')}>Premium</PremiumMaterial>}
            <NormalMaterial selected={requirement.material === 'normal'} onClick={this._onMaterialChange.bind(this, 'normal')}>Basis</NormalMaterial>
            <SpacingMaterialItem />
          </MaterialSelection>
          {this._renderMaterialContent()}
        </ContentContainerBody>
        <SideInformation
          onBackClick={() => store!.goBack()}
          infoText={jaw === 'upper'
            ? 'viele unserer Partner-Implantologen eine Finanzierung der Kosten für eine Implantatversorgung anbieten?'
            : 'eine Implantatversorgung viele Vorteile gegenüber konventionellem Zahnersatz bietet?'
          }
          longInfoText={jaw === 'upper'
            ? 'viele unserer Partner-Implantologen eine Finanzierung der Kosten für eine Implantatversorgung anbieten? Eine hochwertige und vor allem langlebige Implantatversorgung nach einem Zahnverlust bedeutet, sofern keine Zahnzusatzversicherung abgeschlossen wurde, einen hohen Eigenanteil für die Patienten. Dieser kann bei vielen unserer Implantologen langfristig zu Sonderkonditionen finanziert werden. So kann eine zeitnahe Versorgung stattfinden und Knochenabbau und weiterem Zahnverlust kann vorgebeugt werden.'
            : 'eine Implantatversorgung viele Vorteile bietet? Vor allem im Vergleich zu einem konventionellen Zahnersatz?  Eine Implantatversorgung ermöglicht Patienten eine uneingeschränkte Nahrungsaufnahme, ein natürliches Kaugefühl, eine mit eigenen Zähnen vergleichbare Stabilität und Kaukraft und vor allem eine bisher unübertroffene Ästhetik. Zahnimplantate kommen einem natürlichen Gebiss mit Abstand am nächsten und sind die erste Wahl bei Zahnverlust.'
          } />
      </SplitContentContainer>

      <ButtonBar>
        <Button onClick={() => store!.goBack()}>Zurück</Button>
        <SegmentedProgressBar segmentCount={2} segment={0} />
      </ButtonBar>
    </InnerContainer>)
  }
}

const MaterialSelection = styled.div`
  display: flex;
  flex-direction: row;
  height: 65px;
  line-height: 65px;
  align-items: stretch;
`

const MaterialItem = styled.div<{ selected?: boolean }>`
  ${HeadlineCSS};
  cursor: pointer;
  padding: 0 30px;
  border-bottom: 1px solid ${props => props.theme.borderColor};
  opacity: 0.5;
  font-size: 24px !important;
  ${props => props.selected && `
    border-color: transparent;
    opacity: 1;
  `}
  ${breakpoint('tablet')`
    font-size: 30px !important;
  `}
`

const SpacingMaterialItem = styled(MaterialItem)`
  flex: 1;
`

const PremiumMaterial = styled(MaterialItem)`
  ${MaterialItem};
  ${props => props.selected && `
    border-right: 1px solid ${props.theme.borderColor};
  `}
`

const NormalMaterial = styled(MaterialItem)`
  ${MaterialItem};
  ${props => props.selected && `
    border-left: 1px solid ${props.theme.borderColor};
    border-right: 1px solid ${props.theme.borderColor};
  `}
`
